<template>
  <div class="tm-page">
    <div class="tm-page-header">
      <h1 class="page-title">Tasks and Map</h1>
    </div>

    <div class="tm-main">
      <div class="tm-map-layout">
        <div class="map-section">
          <div ref="mapContainerRef" class="map-container" />
          <div
            class="tm-ws-status"
            :class="`tm-ws-status--${wsStatus}`"
            :title="wsStatusTitle"
          >
            {{ wsStatusLabel }}
          </div>
          <div v-if="mapLoading" class="map-overlay map-overlay--loading">
            <LoadingIndicator text="Loading..." class="map-loading-indicator" />
          </div>
          <div v-else-if="mapError" class="map-overlay map-overlay--error">{{ mapError }}</div>
        </div>

        <aside class="inspector-panel">
        <div class="panel-tabs">
          <template v-for="(tab, index) in panelTabs" :key="tab.value">
            <button
              type="button"
              class="panel-tab"
              :class="{ 'panel-tab--active': activePanelTab === tab.value }"
              @click="switchPanelTab(tab.value)"
            >
              {{ tab.label }}
            </button>
            <span v-if="index < panelTabs.length - 1" class="panel-tab-sep">|</span>
          </template>
        </div>

        <div v-if="panelLoading" class="panel-loading">Loading...</div>
        <div v-else-if="panelError" class="panel-error">{{ panelError }}</div>
        <div v-else-if="panelPeople.length === 0" class="panel-empty">No staff found for this date.</div>
        <div v-else class="inspector-list">
          <div
            v-for="person in panelPeople"
            :key="person.id"
            class="inspector-block"
            :class="{ 'inspector-block--expanded': selectedPersonId === person.id }"
          >
            <div class="inspector-row">
              <div
                class="inspector-main"
                :class="{ 'inspector-main--selected': selectedPersonId === person.id }"
                role="button"
                tabindex="0"
                :aria-expanded="selectedPersonId === person.id"
                @click="togglePerson(person.id)"
                @keydown.enter.prevent="togglePerson(person.id)"
                @keydown.space.prevent="togglePerson(person.id)"
              >
                <span class="inspector-toggle" aria-hidden="true">
                  <img
                    v-if="selectedPersonId === person.id"
                    :src="iconTriangleDownBlack"
                    alt=""
                    class="inspector-toggle-icon inspector-toggle-icon--selected"
                  />
                  <img
                    v-else
                    :src="iconTriangleRight"
                    alt=""
                    class="inspector-toggle-icon"
                  />
                </span>
                <div class="inspector-text">
                  <div class="inspector-name-row">
                    <span
                      class="inspector-color-ball"
                      :style="{ backgroundColor: person.color }"
                      aria-hidden="true"
                    />
                    <span class="inspector-name">{{ person.name }}</span>
                  </div>
                  <span class="inspector-count">{{ person.orderCount }} Orders</span>
                </div>
              </div>
            </div>

            <div
              v-if="selectedPersonId === person.id"
              class="order-list"
            >
              <div
                v-for="order in person.orders"
                :key="order.id"
                class="order-row"
                :class="{ 'order-row--selected': selectedOrderId === order.id }"
                role="button"
                tabindex="0"
                :aria-pressed="selectedOrderId === order.id"
                @click.stop="selectOrder(person.id, order.id)"
                @keydown.enter.prevent="selectOrder(person.id, order.id)"
                @keydown.space.prevent="selectOrder(person.id, order.id)"
              >
                <span class="order-sequence">{{ order.sequence }}</span>
                <span
                  class="order-landmark"
                  :style="getTasksMapLandmarkMaskStyle(person.color)"
                  aria-hidden="true"
                />
                <div class="order-content">
                  <div class="order-address">{{ order.address }}</div>
                  <div class="order-meta">
                    <span class="order-id">#{{ order.orderId }}</span>
                    <span class="order-time">
                      <img :src="iconTimer" class="order-time-icon" alt="" aria-hidden="true" />
                      {{ order.time }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import {
  buildTasksMapBoardParams,
  isTasksMapBoardSuccess,
  mapTasksMapBoardToView,
  postTasksMapBoard,
  readTasksMapApiMessage,
} from '@/api/TasksMapBoard'
import {
  DEFAULT_TASKS_MAP_TABS,
  mapTasksMapMetaToView,
  postTasksMapMeta,
  type TasksMapTabView,
} from '@/api/TasksMapMeta'
import {
  getTasksMapLandmarkMaskStyle,
  type TasksMapPanelTab,
  type TasksMapPerson,
  type TasksMapPoint,
} from '@/api/tasksMap'
import { TasksMapMapController } from '@/views/TMPage/map/tasksMapMapController'
import { useTasksMapRealtime } from '@/views/TMPage/useTasksMapRealtime'
import { loadGoogleMaps } from '@/utils/googleMapsLoader'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import iconTriangleRight from '@/assets/右三.png'
import iconTriangleDownBlack from '@/assets/youxia b.png'
import iconTimer from '@/assets/timer@2x.png'
import '@/views/TMPage/map/tmMapNameLabel.css'

function isValidTasksMapPoint(point: TasksMapPoint | null | undefined): point is TasksMapPoint {
  if (!point) return false
  if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return false
  if (point.lat === 0 && point.lng === 0) return false
  return Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180
}

/** 选中后重拉 board 时，保留侧栏里已有的有效订单坐标，避免被空坐标覆盖 */
function mergePeoplePreserveOrderPositions(
  nextPeople: TasksMapPerson[],
  previousPeople: TasksMapPerson[],
): TasksMapPerson[] {
  if (!previousPeople.length) return nextPeople
  const previousById = new Map(previousPeople.map((person) => [person.id, person]))

  return nextPeople.map((person) => {
    const previous = previousById.get(person.id)
    if (!previous) return person

    const previousOrdersByKey = new Map<string, TasksMapPoint>()
    previous.orders.forEach((order) => {
      if (!isValidTasksMapPoint(order.position)) return
      previousOrdersByKey.set(order.id, order.position)
      previousOrdersByKey.set(order.orderId, order.position)
      previousOrdersByKey.set(String(order.sequence), order.position)
    })

    const orders = person.orders.map((order) => {
      if (isValidTasksMapPoint(order.position)) return order
      const preserved =
        previousOrdersByKey.get(order.id) ??
        previousOrdersByKey.get(order.orderId) ??
        previousOrdersByKey.get(String(order.sequence))
      if (!preserved) return order
      return { ...order, position: preserved }
    })

    const position = isValidTasksMapPoint(person.position)
      ? person.position
      : isValidTasksMapPoint(previous.position)
        ? previous.position
        : person.position

    return { ...person, position, orders }
  })
}

const geocodeCache = new Map<string, TasksMapPoint>()

async function geocodeAddress(address: string): Promise<TasksMapPoint | null> {
  const query = address.trim()
  if (!query) return null
  const cached = geocodeCache.get(query)
  if (cached) return cached

  try {
    await loadGoogleMaps()
    const geocoding = (await google.maps.importLibrary('geocoding')) as google.maps.GeocodingLibrary
    const geocoder = new geocoding.Geocoder()
    const response = await geocoder.geocode({
      address: query,
      componentRestrictions: { country: 'CA' },
    })
    const location = response.results[0]?.geometry?.location
    if (!location) return null
    const point = { lat: location.lat(), lng: location.lng() }
    if (!isValidTasksMapPoint(point)) return null
    geocodeCache.set(query, point)
    return point
  } catch (error) {
    console.warn('[tasks-map] geocode failed:', query, error)
    return null
  }
}

/** 订单缺 lat/lng 时，用地址补点，保证能画出人物→订单白线 */
async function ensureSelectedOrdersHavePositions(personId: string): Promise<boolean> {
  if (!personId) return false
  const personIndex = panelPeople.value.findIndex((person) => person.id === personId)
  if (personIndex < 0) return false

  const person = panelPeople.value[personIndex]!
  const ordersNeedingGeo = person.orders.filter(
    (order) => !isValidTasksMapPoint(order.position) && order.address.trim(),
  )
  if (!ordersNeedingGeo.length) return false

  const resolved = await Promise.all(
    ordersNeedingGeo.map(async (order) => ({
      id: order.id,
      position: await geocodeAddress(order.address),
    })),
  )

  let changed = false
  const positionById = new Map(
    resolved
      .filter((item): item is { id: string; position: TasksMapPoint } => item.position != null)
      .map((item) => [item.id, item.position]),
  )
  if (!positionById.size) return false

  const nextOrders = person.orders.map((order) => {
    if (isValidTasksMapPoint(order.position)) return order
    const point = positionById.get(order.id)
    if (!point) return order
    changed = true
    return { ...order, position: point }
  })

  if (!changed) return false

  const nextPeople = [...panelPeople.value]
  nextPeople[personIndex] = { ...person, orders: nextOrders }
  panelPeople.value = nextPeople

  const routeFromOrders = nextOrders
    .filter((order) => isValidTasksMapPoint(order.position))
    .sort((a, b) => a.sequence - b.sequence)
    .map((order) => order.position)
  if (routeFromOrders.length && selectedRoute.value.length < routeFromOrders.length) {
    selectedRoute.value = routeFromOrders
  }
  return true
}

const activePanelTab = ref<TasksMapPanelTab>('inspectors')
const panelTabs = ref<TasksMapTabView[]>([...DEFAULT_TASKS_MAP_TABS])
const panelPeople = ref<TasksMapPerson[]>([])
const panelLoading = ref(true)
const panelError = ref('')
const selectedPersonId = ref('')
const selectedOrderId = ref('')
const selectedRoute = ref<TasksMapPoint[]>([])
const selectedDate = ref(formatTodayYmd())

function formatTodayYmd(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

const mapContainerRef = ref<HTMLDivElement | null>(null)
const mapLoading = ref(true)
const mapError = ref('')

const mapController = new TasksMapMapController()

function renderMapOverlays(): void {
  mapController.render(
    panelPeople.value,
    selectedPersonId.value,
    selectedRoute.value,
    selectedOrderId.value,
  )
}

async function fetchTasksMapBoard(
  tab: TasksMapPanelTab,
  selectedStaffId = '',
  options?: { showLoading?: boolean },
): Promise<void> {
  if (options?.showLoading) panelLoading.value = true
  if (options?.showLoading) panelError.value = ''
  try {
    const previousPeople = panelPeople.value
    const response = await postTasksMapBoard(
      buildTasksMapBoardParams({
        date: selectedDate.value,
        tab,
        selectedStaffId,
      }),
    )

    if (isTasksMapBoardSuccess(response)) {
      const view = mapTasksMapBoardToView(response.data)
      if (view.date) selectedDate.value = view.date
      panelPeople.value = mergePeoplePreserveOrderPositions(view.people, previousPeople)
      selectedRoute.value = view.selectedRoute
      panelError.value = ''

      if (
        selectedPersonId.value &&
        !panelPeople.value.some((person) => person.id === selectedPersonId.value)
      ) {
        selectedPersonId.value = ''
        selectedOrderId.value = ''
        selectedRoute.value = []
      }

      if (
        selectedOrderId.value &&
        !panelPeople.value.some((person) =>
          person.orders.some((order) => order.id === selectedOrderId.value),
        )
      ) {
        selectedOrderId.value = ''
      }

      if (selectedPersonId.value) {
        const geocoded = await ensureSelectedOrdersHavePositions(selectedPersonId.value)
        if (geocoded && !selectedRoute.value.length) {
          const person = panelPeople.value.find((item) => item.id === selectedPersonId.value)
          selectedRoute.value =
            person?.orders
              .filter((order) => isValidTasksMapPoint(order.position))
              .sort((a, b) => a.sequence - b.sequence)
              .map((order) => order.position) ?? []
        }
      }

      renderMapOverlays()
      return
    }

    const message = readTasksMapApiMessage(response)
    panelError.value = message || 'Failed to load staff list.'
    panelPeople.value = []
    selectedRoute.value = []
    renderMapOverlays()
    if (message) {
      console.warn('[tasks-map] board API message:', message)
    }
  } catch (error) {
    console.error('Fetch tasks map board error:', error)
    panelError.value = 'Failed to load staff list.'
    panelPeople.value = []
    selectedRoute.value = []
    renderMapOverlays()
  } finally {
    if (options?.showLoading) panelLoading.value = false
  }
}

async function fetchTasksMapMeta(): Promise<void> {
  try {
    const response = await postTasksMapMeta()
    if (response.code !== 200) return

    const view = mapTasksMapMetaToView(response.data)
    panelTabs.value = view.tabs

    if (!view.tabs.some((tab) => tab.value === activePanelTab.value)) {
      activePanelTab.value = view.tabs[0]?.value ?? 'inspectors'
    }
  } catch (error) {
    console.error('Fetch tasks map meta error:', error)
  }
}

async function loadPanelPeople(tab: TasksMapPanelTab): Promise<void> {
  selectedPersonId.value = ''
  selectedOrderId.value = ''
  selectedRoute.value = []
  await fetchTasksMapBoard(tab, '', { showLoading: true })
}

async function switchPanelTab(tab: TasksMapPanelTab): Promise<void> {
  if (activePanelTab.value === tab) return
  activePanelTab.value = tab
  await loadPanelPeople(tab)
}

async function selectOrder(personId: string, orderId: string): Promise<void> {
  if (selectedPersonId.value !== personId) return

  selectedOrderId.value = selectedOrderId.value === orderId ? '' : orderId
  if (!selectedOrderId.value) {
    renderMapOverlays()
    return
  }

  await ensureSelectedOrdersHavePositions(personId)
  renderMapOverlays()
}

async function togglePerson(id: string): Promise<void> {
  const nextId = selectedPersonId.value === id ? '' : id
  selectedPersonId.value = nextId
  selectedOrderId.value = ''

  // 先用当前侧栏订单坐标立刻连线，再拉 board 补 driver_routes
  if (nextId) {
    const person = panelPeople.value.find((item) => item.id === nextId)
    const localRoute =
      person?.orders
        .filter((order) => isValidTasksMapPoint(order.position))
        .sort((a, b) => a.sequence - b.sequence)
        .map((order) => order.position) ?? []
    selectedRoute.value = localRoute
    renderMapOverlays()

    if (!localRoute.length) {
      const geocoded = await ensureSelectedOrdersHavePositions(nextId)
      if (geocoded) renderMapOverlays()
    }
  } else {
    selectedRoute.value = []
    renderMapOverlays()
  }

  await fetchTasksMapBoard(activePanelTab.value, nextId)
}

function clearSelection(): void {
  if (selectedOrderId.value) {
    selectedOrderId.value = ''
    renderMapOverlays()
    return
  }
  if (!selectedPersonId.value) return
  selectedPersonId.value = ''
  selectedOrderId.value = ''
  selectedRoute.value = []
  void fetchTasksMapBoard(activePanelTab.value, '')
}

async function initMap(): Promise<void> {
  await nextTick()

  const container = mapContainerRef.value
  if (!container) {
    mapLoading.value = false
    mapError.value = 'Map container not ready'
    return
  }

  mapLoading.value = true
  mapError.value = ''

  try {
    await mapController.init(container)
    mapController.setMapClickHandler(clearSelection)
    renderMapOverlays()
  } catch (err) {
    mapError.value = err instanceof Error ? err.message : 'Failed to load Google Maps'
  } finally {
    mapLoading.value = false
  }
}

const wsStatus = useTasksMapRealtime({
  panelPeople,
  selectedPersonId,
  selectedOrderId,
  panelTab: activePanelTab,
  refreshBoard: () =>
    fetchTasksMapBoard(activePanelTab.value, selectedPersonId.value, { showLoading: false }),
  renderMap: renderMapOverlays,
})

const wsStatusLabel = computed(() => {
  switch (wsStatus.value) {
    case 'open':
      return 'WS · Connected'
    case 'connecting':
      return 'WS · Connecting'
    case 'no-token':
      return 'WS · No token'
    case 'closed':
      return 'WS · Reconnecting'
    default:
      return 'WS · Idle'
  }
})

const wsStatusTitle = computed(() => {
  switch (wsStatus.value) {
    case 'open':
      return 'Realtime link to App is up'
    case 'no-token':
      return 'Login token missing; WebSocket will not connect'
    case 'closed':
      return 'Socket closed; auto-reconnect in progress (check Network → WS)'
    default:
      return 'WebSocket status'
  }
})

onMounted(async () => {
  await Promise.all([
    (async () => {
      await fetchTasksMapMeta()
      await loadPanelPeople(activePanelTab.value)
    })(),
    initMap(),
  ])
})

onUnmounted(() => {
  mapController.destroy()
})
</script>

<style scoped>
.tm-page {
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  background: #1d1d1d;
}

.tm-page-header {
  flex-shrink: 0;
  padding: 24px 24px 16px 24px;
  box-sizing: border-box;
  background: #f5f6fb;
  width: 100%;
  margin: 0;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  line-height: 26px;
}

.tm-main {
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.tm-map-layout {
  --tm-map-basis: 920px;
  --tm-panel-width: 300px;
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  gap: 0;
  border-top: 1px solid #e0e0e0;
  overflow: hidden;
  background: #1d1d1d;
}

.map-section {
  position: relative;
  flex: 1 1 var(--tm-map-basis);
  min-width: 0;
  min-height: 0;
  height: 100%;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  overflow: hidden;
  background: #1d1d1d;
  box-sizing: border-box;
}

.map-container {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.tm-ws-status {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #fff;
  background: rgba(0, 0, 0, 0.65);
  pointer-events: none;
  user-select: none;
}

.tm-ws-status--open {
  background: rgba(22, 128, 72, 0.9);
}

.tm-ws-status--connecting,
.tm-ws-status--closed {
  background: rgba(180, 110, 20, 0.9);
}

.tm-ws-status--no-token,
.tm-ws-status--idle {
  background: rgba(140, 40, 40, 0.9);
}

.map-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(29, 29, 29, 0.72);
  color: #ffffff;
  font-size: 14px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.map-overlay--loading :deep(.map-loading-indicator) {
  padding: 0;
}

.map-overlay--loading :deep(.map-loading-indicator span) {
  color: #ffffff;
}

.map-overlay--error {
  padding: 24px;
  text-align: center;
}

.inspector-panel {
  --inspector-content-inset-left: 32px;
  --inspector-selected-inset-left: 24px;
  --inspector-selected-inset-right: 56px;
  --inspector-row-inset-right: 56px;
  --inspector-orders-inset-left: 61px;
  --inspector-name-badge-width: min(
    220px,
    calc(
      100% + var(--inspector-content-inset-left) - var(--inspector-selected-inset-left)
    )
  );
  flex: 0 0 var(--tm-panel-width);
  width: var(--tm-panel-width);
  min-width: 0;
  max-width: var(--tm-panel-width);
  margin: 0;
  min-height: 0;
  height: 100%;
  border: none;
  border-left: 1px solid #5f5f5f;
  border-radius: 0;
  background: #484848;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.panel-tabs {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 0 16px;
  box-sizing: border-box;
}

.panel-tab {
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #cccccc;
  text-align: left;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.panel-tab--active {
  color: #ffffff;
  font-weight: 600;
}

.panel-tab-sep {
  color: #999999;
  font-size: 14px;
}

.panel-loading,
.panel-error,
.panel-empty {
  padding: 16px;
  font-size: 14px;
  line-height: 20px;
  color: #cccccc;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.panel-error {
  color: #ffb4b4;
  word-break: break-word;
}

.panel-empty {
  color: #999999;
}

.inspector-list {
  overflow-y: auto;
  padding: 0 0 0 var(--inspector-content-inset-left);
  box-sizing: border-box;
}

.inspector-block {
  border-bottom: none;
  border-radius: 0;
}

.inspector-block:last-child {
  border-bottom: none;
}

.inspector-row {
  width: 100%;
  min-height: 56px;
  padding: 8px var(--inspector-row-inset-right) 8px 0;
  display: flex;
  align-items: flex-start;
  box-sizing: border-box;
  text-align: left;
}

.inspector-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  text-align: left;
  cursor: pointer;
}

.inspector-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.inspector-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
}

.inspector-color-ball {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
}

.inspector-main--selected {
  position: relative;
  z-index: 0;
  width: var(--inspector-name-badge-width);
  max-width: 100%;
  padding: 5px 12px 5px 0;
  box-sizing: border-box;
}

.inspector-main--selected::before {
  content: '';
  position: absolute;
  left: calc(var(--inspector-selected-inset-left) - var(--inspector-content-inset-left));
  top: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  border-radius: 8px;
  z-index: -1;
  box-sizing: border-box;
}

.inspector-main--selected .inspector-toggle,
.inspector-main--selected .inspector-text {
  position: relative;
  z-index: 1;
}

.inspector-toggle {
  margin: 0;
  padding: 0;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.inspector-toggle-icon {
  width: 5px;
  height: 8px;
  flex-shrink: 0;
  object-fit: contain;
  display: block;
}

.inspector-toggle-icon--selected {
  width: 8px;
  height: 5px;
}

.inspector-block--expanded {
  background: transparent;
}

.inspector-name {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  flex: 1;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.inspector-main--selected .inspector-name {
  color: #000000;
}

.inspector-main--selected .inspector-count {
  color: #484848;
}

.inspector-count {
  padding-left: calc(16px + 8px);
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #cccccc;
  text-align: left;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.order-list {
  padding: 4px 12px 12px calc(var(--inspector-orders-inset-left) - var(--inspector-content-inset-left));
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: transparent;
}

.order-row {
  min-height: 56px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  border-radius: 8px;
  padding: 4px 8px 4px 0;
  box-sizing: border-box;
}

.order-row:hover {
  background: rgba(255, 255, 255, 0.06);
}

.order-row--selected {
  background: rgba(255, 255, 255, 0.1);
}

.order-row:focus-visible {
  outline: 1px solid #ffffff;
  outline-offset: 1px;
  }

.order-sequence {
  width: 12px;
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #cccccc;
  text-align: right;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.order-landmark {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 0;
  display: block;
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.order-content {
  min-width: 0;
  flex: 1;
}

.order-address {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #ffffff;
  text-align: left;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.order-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
}

.order-id {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #cccccc;
  text-align: left;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.order-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #cccccc;
  text-align: left;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.order-time-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  display: block;
  object-fit: contain;
}

@media (max-width: 1100px) {
  .tm-map-layout {
    flex-direction: column;
    min-height: 0;
  }

  .map-section {
    order: 1;
    flex: 1 1 auto;
    min-height: 360px;
    max-width: none;
  }

  .inspector-panel {
    order: 2;
    width: 100%;
    min-width: 0;
    max-width: none;
    flex: 0 0 auto;
    max-height: 45vh;
    border-left: none;
    border-top: 1px solid #5f5f5f;
  }

  .map-container {
    min-height: 360px;
  }
}

@media (max-width: 640px) {
  .tm-page-header {
    padding: 16px 16px 12px;
  }

  .inspector-panel {
    width: 100%;
    flex-basis: auto;
  }
}
</style>
